// Build revenue tracker (Oct 27, 2026) — replaces the Aug 26 "Company build
// revenue" widget on every sales and leadership home board (same registry key,
// `company_revenue`).
//
// Greg: "i want alex to see revenues per month based on build date ... a
// tracker on his home page showing scoped revenue, scheduled revenue and built
// revenue" — then "good on all their home boards individually and company
// wide, they can toggle back and forth."
//
// Per month, three mutually exclusive lines (build_revenue_months):
//   built      roof completed that month
//   scheduled  on the calendar that month, not built yet
//   scoped     scope in, no build date yet; month = the expected (preferred)
//              build date set on the Build Calendar
// Supplements excluded, as before: field roles never see supplement dollars.
// Mine = the viewer's jobs, half on a split. Company = every job. Sums only.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchBuildRevenue,
  fmtJobs,
  fmtMoney,
  monthLong,
  monthShort,
  type BuildRevenue,
  type RevenueMonth,
  type RevenueScope,
} from "../../../lib/buildRevenue";

const COMPANY_DEFAULT_ROLES = new Set([
  "asm", "admin", "sales_director", "director_of_operations", "roof_director", "finance",
]);

const COLORS = {
  built: "bg-emerald-500",
  scheduled: "bg-sky-500",
  scoped: "bg-amber-400",
};

function scopeKey(memberId: string) {
  return `bp_buildrev_scope_${memberId}`;
}

function readScope(memberId: string, role: string | null | undefined): RevenueScope {
  try {
    const v = localStorage.getItem(scopeKey(memberId));
    if (v === "mine" || v === "company") return v;
  } catch {
    /* private window */
  }
  return role && COMPANY_DEFAULT_ROLES.has(role) ? "company" : "mine";
}

export default function BuildRevenueWidget({
  memberId,
  role,
}: {
  memberId: string;
  role: string | null | undefined;
}) {
  const [scope, setScope] = useState<RevenueScope>(() => readScope(memberId, role));
  const [data, setData] = useState<BuildRevenue | null>(null);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    fetchBuildRevenue(scope).then(
      (d) => !cancelled && setData(d),
      (e) => {
        console.warn("build_revenue_months failed", e);
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const choose = (s: RevenueScope) => {
    setScope(s);
    try {
      localStorage.setItem(scopeKey(memberId), s);
    } catch {
      /* ignore */
    }
  };

  const nowYm = data?.today.slice(0, 7) ?? "";
  const byYm = useMemo(() => new Map((data?.months ?? []).map((m) => [m.ym, m])), [data]);
  // Two months back, this month, three ahead.
  const strip = useMemo(() => {
    const ms = data?.months ?? [];
    const i = ms.findIndex((m) => m.ym === nowYm);
    return i < 0 ? ms.slice(-6) : ms.slice(Math.max(0, i - 2), i + 4);
  }, [data, nowYm]);
  const sel: RevenueMonth | undefined = byYm.get(picked ?? nowYm);
  const max = Math.max(1, ...strip.map((m) => m.built + m.scheduled + m.scoped));

  const yr = nowYm.slice(0, 4);
  const ytdRows = (data?.months ?? []).filter((m) => m.ym.startsWith(`${yr}-`) && m.ym <= nowYm);
  const ytd = ytdRows.reduce((s, m) => s + m.built, 0);
  const ytdN = ytdRows.reduce((s, m) => s + m.builtN, 0);
  const lastYtd = ytdRows
    .map((m) => byYm.get(`${Number(yr) - 1}${m.ym.slice(4)}`)?.built ?? 0)
    .reduce((s, v) => s + v, 0);
  const yoy = lastYtd > 0 ? ((ytd - lastYtd) / lastYtd) * 100 : null;

  const header = (
    <div className="flex items-center gap-2 mb-0.5">
      <span>🏗️</span>
      <span className="font-bold text-sm text-bardin-ink">Build revenue</span>
      <div className="ml-auto inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 text-[11px] font-bold">
        {(["mine", "company"] as RevenueScope[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => choose(s)}
            className={`px-2.5 py-0.5 rounded-full ${
              scope === s ? "bg-white text-bardin-ink shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            aria-pressed={scope === s}
          >
            {s === "mine" ? "Mine" : "Company"}
          </button>
        ))}
      </div>
    </div>
  );

  // A failed read must never render as $0 — that reads as "we sold nothing".
  if (failed) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        {header}
        <div className="text-xs text-slate-500 mt-2">Couldn't load right now. Try a refresh.</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        {header}
        <div className="h-16 rounded bg-slate-100 animate-pulse mt-3" />
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded bg-slate-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const total = sel ? sel.built + sel.scheduled + sel.scoped : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      {header}
      <div className="text-[11px] text-slate-400 mb-3">
        {scope === "mine" ? "Your jobs (half on a split)" : "Every job"} · by build month · supplements excluded
      </div>

      {/* Month strip: stacked built / scheduled / scoped. Tap a month. */}
      <div className="flex items-end gap-1.5 h-20">
        {strip.map((m) => {
          const t = m.built + m.scheduled + m.scoped;
          const on = (picked ?? nowYm) === m.ym;
          return (
            <button
              key={m.ym}
              type="button"
              onClick={() => setPicked(m.ym === nowYm ? null : m.ym)}
              className={`flex-1 h-full flex flex-col justify-end rounded-md px-0.5 pb-0.5 ${
                on ? "bg-slate-100 ring-1 ring-slate-300" : "hover:bg-slate-50"
              }`}
              title={`${monthLong(m.ym)}: built ${fmtMoney(m.built)} · scheduled ${fmtMoney(m.scheduled)} · scoped ${fmtMoney(m.scoped)}`}
              aria-label={`${monthLong(m.ym)}, ${fmtMoney(t)}`}
            >
              <div className="w-full flex flex-col-reverse rounded-t overflow-hidden" style={{ height: `${(t / max) * 100}%` }}>
                {m.built > 0 && <div className={COLORS.built} style={{ height: `${(m.built / t) * 100}%` }} />}
                {m.scheduled > 0 && <div className={COLORS.scheduled} style={{ height: `${(m.scheduled / t) * 100}%` }} />}
                {m.scoped > 0 && <div className={COLORS.scoped} style={{ height: `${(m.scoped / t) * 100}%` }} />}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex gap-1.5 mt-1">
        {strip.map((m) => (
          <div
            key={m.ym}
            className={`flex-1 text-center text-[10px] ${m.ym === nowYm ? "font-bold text-bardin-ink" : "text-slate-400"}`}
          >
            {monthShort(m.ym)}
          </div>
        ))}
      </div>

      {/* Selected month */}
      {sel && (
        <div className="mt-3">
          <div className="flex items-baseline gap-2 mb-1.5">
            <span className="text-xs font-bold text-bardin-ink">{monthLong(sel.ym)}</span>
            {sel.ym === nowYm && <span className="text-[10px] font-bold text-orange-600">THIS MONTH</span>}
            <span className="ml-auto text-xs text-slate-500">
              Total <b className="text-bardin-ink tabular-nums">{fmtMoney(total)}</b>
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Tile color={COLORS.built} label="Built" amount={sel.built} n={sel.builtN} />
            <Tile color={COLORS.scheduled} label="Scheduled" amount={sel.scheduled} n={sel.scheduledN} />
            <Tile color={COLORS.scoped} label="Scoped" sub="expected" amount={sel.scoped} n={sel.scopedN} />
          </div>
        </div>
      )}

      <div className="mt-3 space-y-1 text-[11.5px]">
        {data.noDate.n > 0 && (
          <Link to="/build-calendar" className="flex items-center gap-2 text-slate-600 hover:text-bardin-ink">
            <span className={`inline-block w-2 h-2 rounded-full ${COLORS.scoped}`} />
            <span>
              <b>{fmtJobs(data.noDate.n)}</b> scoped job{data.noDate.n === 1 ? "" : "s"} with no expected date ·{" "}
              {fmtMoney(data.noDate.scoped)}
            </span>
            <span className="ml-auto font-bold text-bardin-teal">Plan dates →</span>
          </Link>
        )}
        {data.pastDue.n > 0 && (
          <Link to="/build-calendar" className="flex items-center gap-2 text-amber-700 hover:text-amber-800">
            <span>⚠</span>
            <span>
              <b>{fmtJobs(data.pastDue.n)}</b> build{data.pastDue.n === 1 ? "" : "s"} past their date, not marked built ·{" "}
              {fmtMoney(data.pastDue.scheduled)}
            </span>
          </Link>
        )}
        <div className="flex items-center gap-2 text-slate-500 pt-1 border-t border-slate-100">
          <span>
            {yr} built <b className="text-bardin-ink tabular-nums">{fmtMoney(ytd)}</b> · {fmtJobs(ytdN)} build
            {ytdN === 1 ? "" : "s"}
          </span>
          {yoy !== null && (
            <span
              className={`ml-auto px-1.5 py-0.5 rounded-full text-[10.5px] font-bold border ${
                yoy >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
              }`}
              title={`vs ${fmtMoney(lastYtd)} over the same months last year`}
            >
              {yoy >= 0 ? "▲" : "▼"} {Math.abs(yoy).toFixed(0)}% vs last year
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ color, label, sub, amount, n }: { color: string; label: string; sub?: string; amount: number; n: number }) {
  return (
    <div className="rounded-lg border border-slate-200 px-2 py-1.5">
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
        <span className={`inline-block w-2 h-2 rounded-sm ${color}`} />
        {label}
        {sub && <span className="normal-case font-normal tracking-normal text-slate-400">· {sub}</span>}
      </div>
      <div className="text-base font-extrabold tabular-nums text-bardin-ink leading-tight">{fmtMoney(amount)}</div>
      <div className="text-[10.5px] text-slate-400">
        {fmtJobs(n)} job{n === 1 ? "" : "s"}
      </div>
    </div>
  );
}
